import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../../core/helpers/cn";
import FirebaseService from "../../core/services/firebase.service";
import BottomSheetPopup from "../BottomSheetPopup/BottomSheetPopup";
import FirebaseKey from "../FirebaseKey/FirebaseKey";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 30;

interface OtpPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: (
    otp: string,
    setError: (error: boolean) => void
  ) => boolean | void | Promise<boolean | void>;
  onResend?: () => void;
}

const OtpPopup = ({
  isOpen,
  onClose,
  onContinue,
  onResend,
}: OtpPopupProps) => {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const otpValue = digits.join("");

  const startResendCooldown = useCallback(() => {
    setResendSeconds(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setDigits(Array(OTP_LENGTH).fill(""));
    setError(false);
    startResendCooldown();
  }, [isOpen, startResendCooldown]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRefs.current[0]?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const t = setInterval(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendSeconds]);

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      const paste = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
      const arr = paste.split("").concat(Array(OTP_LENGTH).fill("")).slice(0, OTP_LENGTH);
      setDigits(arr);
      setError(false);
      const next = Math.min(paste.length, OTP_LENGTH - 1);
      inputRefs.current[next]?.focus();
      return;
    }
    const char = value.replace(/\D/g, "");
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    setError(false);
    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpValue.length !== OTP_LENGTH) {
      setError(true);
      return;
    }
    setError(false);
    const shouldClose = await onContinue?.(otpValue, setError);
    if (shouldClose !== false) {
      onClose();
    }
  };

  const handleResend = () => {
    if (resendSeconds > 0) return;
    onResend?.();
    startResendCooldown();
  };

  return (
    <BottomSheetPopup isOpen={isOpen} onClose={onClose}>
      <h2 className="text-[32px] font-semibold text-black mb-6">
        <FirebaseKey firebaseKey={FirebaseService.otp_title} />
      </h2>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div
          className={cn(
            "w-full flex items-center justify-center gap-2 p-4 rounded-full transition-colors",
            "bg-[rgba(128,126,125,0.12)]"
          )}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={i === 0 ? 6 : 1}
              value={d}
              placeholder="–"
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className={cn(
                "w-10 h-12 rounded-xl text-center text-lg font-medium border-0 focus:outline-none bg-transparent  placeholder:text-[rgba(67,66,66,0.18)]",
                error && "text-[#FF383C] placeholder:text-[#FF383C]/60"
              )}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>
        <p className="text-sm text-[#FF383C] mb-6">
          {error && (
            <FirebaseKey firebaseKey={FirebaseService.otp_warning} />
          )}
        </p>
        <button
          type="submit"
          className="w-full p-4 mb-2 rounded-full bg-[#0088FF] text-white font-medium transition-colors"
        >
          <FirebaseKey firebaseKey={FirebaseService.otp_button} />
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendSeconds > 0}
          className={cn(
            "w-full p-4 rounded-full font-medium transition-colors",
            resendSeconds > 0
              ? "bg-[rgba(128,126,125,0.12)] text-[rgba(67,66,66,0.40)] cursor-not-allowed"
              : "bg-[rgba(128,126,125,0.12)] text-black hover:bg-[rgba(128,126,125,0.2)] active:bg-[rgba(128,126,125,0.25)]"
          )}
        >
          <FirebaseKey firebaseKey={FirebaseService.otp_resend_button} />
          {resendSeconds > 0 && ` (0:${String(resendSeconds).padStart(2, "0")})`}
        </button>
      </form>
    </BottomSheetPopup>
  );
};

export default OtpPopup;
